import React, { useCallback, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { DefeitosVerveTableRowV2 } from './DefeitosVerveTableRowV2';
import { DV2_GROUPS, categorizeDefeitoRow, DEFEITOS_COL_TOGGLE_KEYS, DEFEITOS_COL_LABELS, DEFEITOS_DV2_CENTER_COL_KEYS } from './defeitosVerveShared';
import './CondicoesIntervencaoTableV2.css';
import './DefeitosVerveTableV2.css';

export function DefeitosVerveTableV2({ rows, totalRows = 0, api }) {
  const [collapsedGroups, setCollapsedGroups] = useState({ desabilitada: true });

  const groupedRows = useMemo(() => {
    const map = { habilitada: [], desabilitada: [] };
    rows.forEach((row) => {
      const cat = categorizeDefeitoRow(row);
      map[cat].push(row);
    });
    return map;
  }, [rows]);

  const groupsWithRows = useMemo(
    () => DV2_GROUPS.filter((g) => (groupedRows[g.key]?.length ?? 0) > 0),
    [groupedRows]
  );

  const visibleColCount = useMemo(() => {
    let n = 2;
    DEFEITOS_COL_TOGGLE_KEYS.forEach((k) => {
      if (api.isDefeitoColVis(k)) n += 1;
    });
    return n;
  }, [api.isDefeitoColVis]);

  const codigoVisible = api.isDefeitoColVis('codigo');

  const allCollapsed = useMemo(
    () => groupsWithRows.length > 0 && groupsWithRows.every((g) => !!collapsedGroups[g.key]),
    [groupsWithRows, collapsedGroups]
  );

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleAllGroups = useCallback(() => {
    const nextAllCollapsed = !allCollapsed;
    const nextCollapsed = {};
    groupsWithRows.forEach((g) => {
      nextCollapsed[g.key] = nextAllCollapsed;
    });
    setCollapsedGroups(nextCollapsed);
  }, [allCollapsed, groupsWithRows]);

  const isGroupCollapsed = (key) => !!collapsedGroups[key];

  if (!rows.length && totalRows === 0) {
    return (
      <div className="dv2-root ci2-root">
        <div className="ci2-panel">
          <div className="ci2-foot-note">Nenhum defeito encontrado.</div>
        </div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="dv2-root ci2-root">
        <div className="ci2-panel">
          <div className="ci2-foot-note">Nenhum resultado para a pesquisa.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`dv2-root ci2-root${codigoVisible ? ' dv2-code-visible' : ''}`}>
      <div className="ci2-panel">
        <div className="ci2-table-scroll">
          <table className="ci2-table dv2-table">
            <thead>
              <tr>
                <th>Habilitar</th>
                {codigoVisible ? <th className="dv2-th-code">Código</th> : null}
                <th className="dv2-th-nome">Defeito</th>
                {DEFEITOS_COL_TOGGLE_KEYS.filter((k) => k !== 'codigo' && api.isDefeitoColVis(k)).map((k) => (
                  <th key={k} className={DEFEITOS_DV2_CENTER_COL_KEYS.includes(k) ? 'dv2-th-center' : undefined}>
                    {DEFEITOS_COL_LABELS[k]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DV2_GROUPS.map((group) => {
                const groupRows = groupedRows[group.key] || [];
                if (!groupRows.length) return null;
                const collapsed = isGroupCollapsed(group.key);
                const dotStyle =
                  group.key === 'desabilitada'
                    ? { border: `1.5px dashed ${group.color}`, background: 'transparent' }
                    : { background: group.color };

                return (
                  <React.Fragment key={group.key}>
                    <tr
                      className={`ci2-group-row${collapsed ? ' collapsed' : ''}`}
                      onClick={() => toggleGroup(group.key)}
                    >
                      <td colSpan={visibleColCount}>
                        <div className="ci2-group-label">
                          <span className="ci2-chev">
                            <ChevronDown size={13} strokeWidth={2.5} />
                          </span>
                          <span className="ci2-dot" style={dotStyle} />
                          {group.label}
                          <span className="ci2-group-count">· {groupRows.length}</span>
                          <span className="ci2-group-desc">{group.desc}</span>
                        </div>
                      </td>
                    </tr>
                    {groupRows.map((row) => (
                      <DefeitosVerveTableRowV2
                        key={String(row.TB01048_CODIGO).trim()}
                        row={row}
                        cat={group.key}
                        api={api}
                        hidden={collapsed}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="ci2-foot-note">
          <span>
            {rows.length} defeito{rows.length !== 1 ? 's' : ''} · {groupsWithRows.length} grupos
          </span>
          <button
            type="button"
            className="board-clickup-filter-chip ci2-foot-toggle"
            onClick={toggleAllGroups}
            aria-label={allCollapsed ? 'Expandir todos os grupos' : 'Recolher todos os grupos'}
          >
            <span className="board-clickup-filter-chip-text">
              <span className="board-clickup-filter-chip-label">
                {allCollapsed ? 'Expandir grupos' : 'Recolher grupos'}
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
